import {
  AdminNotetakerErasureRequest,
  BulkInviteRequest,
  BulkInviteResponse,
  ContactAdminRequest,
  ContactAdminResponse,
  ErasureStartResponse,
  ErasureStatusResponse,
  InvitationResendResult,
  InviteRequest,
  InviteResponse,
  InvitesResponse,
  MemberResponse,
  MembersResponse,
  MoveMemberRequest,
  ResendInvitationsRequest,
  ResendInvitationsResponse,
  SelfInviteRequest,
  SelfInviteResponse,
  TeamResponse,
  UpdateMemberRoleRequest,
} from './generated'
import { fetchAPI } from '.'

import { TeamInviteLinkResponse } from '@/types'

export type ResendInvitationResult = InvitationResendResult

/** Server cap on `invitation_ids` per request. */
export const RESEND_INVITATIONS_MAX_IDS = 100
const RESEND_INVITATIONS_TIMEOUT_MS = 60_000

export async function listMembers() {
  return fetchAPI<MembersResponse>({
    method: 'GET',
    url: '/members',
  })
}

export async function updateMemberRole(userId: string, data: UpdateMemberRoleRequest) {
  return fetchAPI<MemberResponse>({ method: 'PATCH', url: `/members/${userId}`, data })
}

export async function removeMember(userId: string) {
  return fetchAPI<{ message: string }>({
    method: 'DELETE',
    url: `/members/${userId}`,
  })
}

export async function startMemberNotetakerErasure(userId: string, data: AdminNotetakerErasureRequest) {
  return fetchAPI<ErasureStartResponse>({
    method: 'POST',
    url: `/members/${userId}/notetaker-data-erasure`,
    data,
    expectedStatuses: [403, 404, 422, 429, 503],
  })
}

export async function getMemberNotetakerErasureStatus(userId: string) {
  return fetchAPI<ErasureStatusResponse>({
    method: 'GET',
    url: `/members/${userId}/notetaker-data-erasure`,
    expectedStatuses: [403, 404, 429],
  })
}

// POST /api/v1/teams/{team_id}/members — team-move endpoint lives under the
// teams router (not enterprise). Pass the full URL so fetchAPI's auto-prefix
// doesn't double up.
export async function moveMemberToTeam(teamId: string, data: MoveMemberRequest) {
  return fetchAPI<TeamResponse>({
    method: 'POST',
    url: `/api/v1/teams/${encodeURIComponent(teamId)}/members`,
    data,
  })
}

export async function inviteUser(data: InviteRequest) {
  return fetchAPI<InviteResponse>({
    method: 'POST',
    url: '/invitations',
    data,
  })
}

export async function bulkInviteUsers(data: BulkInviteRequest) {
  return fetchAPI<BulkInviteResponse>({
    method: 'POST',
    url: '/invitations/bulk',
    data,
  })
}

export async function inviteSelf(data: SelfInviteRequest) {
  return fetchAPI<SelfInviteResponse>({
    method: 'POST',
    url: '/self-invite',
    data,
  })
}

export async function contactAdmin(data: ContactAdminRequest) {
  return fetchAPI<ContactAdminResponse>({
    method: 'POST',
    url: '/contact-admin',
    data,
  })
}

export async function listInvites() {
  return fetchAPI<InvitesResponse>({
    method: 'GET',
    url: '/invitations',
  })
}

export async function revokeInvite(email: string) {
  return fetchAPI({
    method: 'DELETE',
    url: `/invitations/${email}`,
    data: {},
  })
}

// Sends synchronously server-side (up to 100 emails), so it needs more than the 20s default.
export async function resendInvitations(data: ResendInvitationsRequest) {
  return fetchAPI<ResendInvitationsResponse>({
    method: 'POST',
    url: '/invitations/resend',
    data,
    timeout: RESEND_INVITATIONS_TIMEOUT_MS,
  })
}

export async function getTeamInviteLink() {
  return fetchAPI<TeamInviteLinkResponse>({
    method: 'GET',
    url: '/invite-link',
  })
}
